import { useState, type JSX } from 'react';
import { applyTheme, storedTheme, type Theme } from '../lib/theme.ts';

/**
 * The light switch, beside the bell.
 *
 * One button rather than a menu of three. "Dark, light, follow the system" is the honest set, but
 * it needs a label to be usable and the bar has no room for one; a single button whose icon shows
 * what you will get if you press it says the same thing in the space available.
 *
 * The theme is applied to the document rather than held in React state that renders colours,
 * because the stylesheet already knows both palettes. This only flips the attribute they hang on.
 */
export function ThemeToggle(): JSX.Element {
  const [theme, setTheme] = useState<Theme>(() => storedTheme());

  const next: Theme = theme === 'dark' ? 'light' : 'dark';

  function flip(): void {
    applyTheme(next);
    setTheme(next);
  }

  return (
    <button
      type="button"
      className="themetoggle"
      onClick={flip}
      // The label names what pressing it does, not what is currently on: a control that announces
      // its own state leaves a screen reader user to work out the verb themselves.
      aria-label={next === 'light' ? 'Включить светлую тему' : 'Включить тёмную тему'}
      title={next === 'light' ? 'Светлая тема' : 'Тёмная тема'}
    >
      {/* Drawn rather than typed: an emoji sun is a different picture on every system. */}
      {next === 'light' ? (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2z" />
        </svg>
      )}
    </button>
  );
}
